import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isNil } from 'ramda';

import TransactionPresenter from '../../../../presenters/TransactionPresenter';
import CashbackPresenter from '../../../../presenters/CashbackPresenter';
import StorePresenter from '../../../../presenters/StorePresenter';
import { Dialog } from '../../../../components';

import TransactionInfo from './components/TransactionInfo';
import EditTransactionModal from './components/EditTransactionModal';
import TransactionFormHeader from './components/TransactionFormHeader';

const Transaction = (props) => {
  const {
    transaction,
    onCashbackCreate,
    onTransactionUpdate,
    onCashbackDelete,
    cashbackErrors,
    transactionErrors,
    onTransactionLoad,
    stores,
  } = props;
  const { t } = useTranslation();
  const [isCashbackDialogOpen, setIsCashbackDialogOpen] = useState(false);
  const [isWarningDialogOpen, setIsWarningDialogOpen] = useState(false);
  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [ignoreMembership, setIgnoreMembership] = useState(false);
  const [errors, setErrors] = useState({});
  const storeId = TransactionPresenter.storeId(transaction);
  const isPending = TransactionPresenter.isPending(transaction);
  const transactionId = TransactionPresenter.id(transaction);
  const isActive = TransactionPresenter.isActive(transaction);
  const cashback = TransactionPresenter.cashback(transaction);

  const handleCashbackDialogOpen = () => setIsCashbackDialogOpen(true);
  const handleCashbackDialogClose = () => setIsCashbackDialogOpen(false);
  const handleWarningDialogOpen = () => setIsWarningDialogOpen(true);
  const handleWarningDialogClose = () => {
    setIsWarningDialogOpen(false);
    setIgnoreMembership(false);
  };
  const handleEditModalOpen = () => setIsEditModalOpen(true);
  const handleEditModalClose = () => setIsEditModalOpen(false);
  const handleDeletionDialogOpen = () => setIsDeletionDialogOpen(true);
  const handleDeletionDialogClose = () => setIsDeletionDialogOpen(false);

  const warning = () => {
    if (isPending) return t('transaction.dialogs.warnings.pendingTransaction');
    if (isNil(storeId)) return t('transaction.dialogs.warnings.updateStore');

    return '';
  };

  const handleCreateCashbackButtonClick = () => {
    if (isPending || isNil(storeId)) return handleWarningDialogOpen();

    return handleCashbackDialogOpen();
  };

  const handleCashbackCreate = async () => {
    const params = {
      plaidTransactionId: transactionId,
      date: TransactionPresenter.date(transaction),
      ignoreMembership,
    };
    await onCashbackCreate(params);
    handleCashbackDialogClose();
    onTransactionLoad(transactionId);
  };

  const handleCashbackDelete = async () => {
    const cashbackId = CashbackPresenter.id(cashback);
    await onCashbackDelete(cashbackId);
    handleDeletionDialogClose();
    onTransactionLoad(transactionId);
  };

  const handleTransactionUpdate = async (values, _actions) => {
    await onTransactionUpdate(transactionId, values);

    handleEditModalClose();
    onTransactionLoad(transactionId);
  };

  const defaultCreateCashbackWarning = t(
    'transaction.dialogs.warnings.createCashback'
  );
  const ignoreMembershipWarning = t(
    'transaction.dialogs.warnings.forceCreateCashback'
  );
  const createCashbackWarning = ignoreMembership
    ? ignoreMembershipWarning
    : defaultCreateCashbackWarning;

  useEffect(() => {
    if (isNil(cashbackErrors)) return;
    if (cashbackErrors.invalid_membership) return setIgnoreMembership(true);

    setErrors(cashbackErrors);
    return () => {
      setErrors({});
    };
  }, [cashbackErrors]);

  useEffect(() => {
    if (!isNil(transactionErrors)) {
      setErrors(transactionErrors);
    }
    return () => {
      setErrors({});
    };
  }, [transactionErrors]);

  return (
    <div className="transaction">
      <TransactionFormHeader
        onCreateCashbackButtonClick={handleCreateCashbackButtonClick}
        onDeleteCashbackButtonClick={handleDeletionDialogOpen}
        onEditTransactionButtonClick={handleEditModalOpen}
        showCreateCashbackButton={isNil(cashback) && isActive}
        showDeleteCashbackButton={!isNil(cashback)}
      />
      <TransactionInfo transaction={transaction} cashback={cashback} />
      <Dialog
        isOpen={isCashbackDialogOpen}
        message={createCashbackWarning}
        onConfirm={handleCashbackCreate}
        onCancel={handleCashbackDialogClose}
        errors={errors}
      />
      <Dialog
        className="warning"
        isOpen={isWarningDialogOpen}
        message={warning()}
        onConfirm={handleWarningDialogClose}
        onCancel={handleWarningDialogClose}
      />
      <Dialog
        className="warning"
        isOpen={isDeletionDialogOpen}
        message={t('transaction.dialogs.warnings.deleteCashback')}
        onConfirm={handleCashbackDelete}
        onCancel={handleDeletionDialogClose}
      />
      <EditTransactionModal
        transaction={transaction}
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        onTransactionUpdate={handleTransactionUpdate}
        cashback={cashback}
        stores={stores}
      />
    </div>
  );
};

Transaction.propTypes = {
  transaction: TransactionPresenter.shape(),
  onCashbackCreate: PropTypes.func.isRequired,
  onCashbackDelete: PropTypes.func.isRequired,
  onTransactionUpdate: PropTypes.func.isRequired,
  onTransactionLoad: PropTypes.func.isRequired,
  cashbackErrors: PropTypes.shape({}),
  stores: PropTypes.arrayOf(StorePresenter.shape()),
  transactionErrors: PropTypes.shape({}),
};

Transaction.defaultProps = {
  transaction: null,
  cashbackErrors: {},
  transactionErrors: {},
  stores: [],
};

export default Transaction;
